import React from 'react';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import { formatDate } from '../../../utils/formatters';

const OrderDetailsModal = ({
    order,
    isOpen,
    onClose,
    language = 'en',
    translations = {},
    formatCurrency,
    loading = false
}) => {
    if (!isOpen || !order) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center border-b pb-3">
                    <h3 className="text-lg font-medium">
                        {(translations && translations[language]?.order_details) || (language === 'ar' ? 'تفاصيل الطلب' : 'Order Details')} #{order.order_number}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500"
                    >
                        <FaTimes className="h-6 w-6" />
                    </button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <h4 className="font-medium text-gray-700">
                            {(translations && translations[language]?.customer_info) || (language === 'ar' ? 'معلومات العميل' : 'Customer Information')}
                        </h4>
                        <p className="text-gray-600">
                            {(translations && translations[language]?.name) || (language === 'ar' ? 'الاسم' : 'Name')}: {order.customer_name || (language === 'ar' ? 'عميل عابر' : 'Walk-in Customer')}
                        </p>
                        {order.customer_phone && (
                            <p className="text-gray-600">
                                {(translations && translations[language]?.phone) || 'Phone'}: {order.customer_phone}
                            </p>
                        )}
                    </div>
                    <div>
                        <h4 className="font-medium text-gray-700">
                            {(translations && translations[language]?.order_info) || (language === 'ar' ? 'معلومات الطلب' : 'Order Information')}
                        </h4>
                        <p className="text-gray-600">
                            {(translations && translations[language]?.date) || (language === 'ar' ? 'التاريخ' : 'Date')}: {formatDate(order.created_at, language)}
                        </p>
                        <p className="text-gray-600">
                            {(translations && translations[language]?.payment_method) || (language === 'ar' ? 'طريقة الدفع' : 'Payment Method')}: {
                                order.is_card_order 
                                    ? (language === 'ar' ? 'بطاقة' : 'Card')
                                    : (language === 'ar' ? 'نقداً' : 'Cash')
                            }
                        </p>
                        {order.status === 'returned' && (
                            <p className="text-red-600">
                                {(translations && translations[language]?.returned_at) || 'Returned At'}: {formatDate(order.returned_at, language)}
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-6">
                    <h4 className="font-medium text-gray-700 mb-3">
                        {(translations && translations[language]?.order_items) || (language === 'ar' ? 'محتويات الطلب' : 'Order Items')}
                    </h4>
                    {loading ? (
                        <div className="flex justify-center items-center p-8">
                            <FaSpinner className="animate-spin text-blue-500 text-4xl" />
                            <span className="ml-2 text-gray-600">{(translations && translations[language]?.loading) || 'Loading...'}</span>
                        </div>
                    ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className={`px-6 py-3 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase`}>
                                        {(translations && translations[language]?.item) || (language === 'ar' ? 'الصنف' : 'Item')}
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        {(translations && translations[language]?.quantity) || (language === 'ar' ? 'الكمية' : 'Quantity')}
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        {(translations && translations[language]?.price) || (language === 'ar' ? 'السعر' : 'Price')}
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        {(translations && translations[language]?.total) || (language === 'ar' ? 'الإجمالي' : 'Total')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {order.items && order.items.length > 0 ? order.items.map((item, index) => (
                                    <tr key={index}>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                            {language === 'ar' ? item.name_ar : item.name}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                            {item.quantity}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                            {formatCurrency(item.price)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                            {formatCurrency(item.price * item.quantity)}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                                            {(translations && translations[language]?.no_items) || 'No items found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {order.items && order.items.length > 0 && (
                            <tfoot className="bg-gray-50">
                                <tr>
                                    <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                                        {(translations && translations[language]?.subtotal_before_tax) || (language === 'ar' ? 'المجموع الفرعي (قبل الضريبة)' : 'Subtotal (Before Tax)')}
                                    </td>
                                    <td className="px-6 py-3 text-right text-sm text-gray-900">
                                        {formatCurrency(order.subtotal)}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                                        {(translations && translations[language]?.tax_percentage) || (language === 'ar' ? 'الضريبة' : 'Tax')} ({order.tax_rate || 15}%)
                                    </td>
                                    <td className="px-6 py-3 text-right text-sm text-gray-900">
                                        {formatCurrency(order.tax)}
                                    </td>
                                </tr>
                                {order.service_charge > 0 && (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                                            {(translations && translations[language]?.service_charge) || 'Service Charge'}
                                        </td>
                                        <td className="px-6 py-3 text-right text-sm text-gray-900">
                                            {formatCurrency(order.service_charge)}
                                        </td>
                                    </tr>
                                )}
                                {order.delivery_fee > 0 && (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                                            {(translations && translations[language]?.delivery_fee) || 'Delivery Fee'}
                                        </td>
                                        <td className="px-6 py-3 text-right text-sm text-gray-900">
                                            {formatCurrency(order.delivery_fee)}
                                        </td>
                                    </tr>
                                )}
                                <tr>
                                    <td colSpan="3" className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                                        {(translations && translations[language]?.total_with_tax) || (language === 'ar' ? 'المجموع (شامل الضريبة)' : 'Total (Including Tax)')}
                                    </td>
                                    <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                                        {formatCurrency(order.total)}
                                    </td>
                                </tr>
                            </tfoot>
                            )}
                        </table>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsModal;
